/**
 * 画布素材选择弹窗
 *
 * 三个范围 tab：
 *  - 本项目素材：收藏时记录的画布项目与当前项目匹配的收藏
 *  - 全局素材：全部收藏（跨项目可挑，含存量无项目标记的老数据）
 *  - 所有素材：当前账号全部生成结果（图片走 /api/images、视频走 /api/videos，
 *    均懒加载最近若干条，不可删除）
 *
 * 选择后创建对应节点；传入 onPick 时改为把选中素材交给调用方（如空图片 / 空视频节点
 * 回填本节点），assetFilter 可限定素材类型。
 */
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Image as ImageIcon, Film, Type, Trash2, Plus, FolderOpen, Globe, Images, Loader2 } from 'lucide-react';
import { assetStorage, type SavedAsset } from './assetStorage';
import { boardActions, getBoardState } from './state/boardStore';
import { makeNode } from './utils';
import { toPersistableMediaUrl } from './utils/persistableMediaUrl';
import { formatBeijingDate } from '../../utils/time';
import { resolveBrowserMediaUrl } from '../../utils/storageUrl';
import { api } from '../../services/api';
import type { JobStatus } from '../../services/api';
import { videoApi } from '../../baselines/video/services/videoApi';
import type { VideoJobUserView } from '../../baselines/video/services/videoApi';

type AssetType = SavedAsset['type'];

interface AssetPickerModalProps {
  open: boolean;
  onClose: () => void;
  /** 只展示指定类型的素材（如空图片节点只看图片、空视频节点只看视频） */
  assetFilter?: AssetType;
  /** 自定义选中回调；不传时默认在画布 (100,100) 新建对应节点 */
  onPick?: (asset: SavedAsset) => void;
}

type AssetScope = 'project' | 'global' | 'tasks';

type FilterValue = 'all' | AssetType;

/** 生成任务（图片）结果 → 统一的插入素材形态（content 为清洗后的持久网关地址） */
function jobToAsset(job: JobStatus): SavedAsset {
  const raw = job.resultUrl || '';
  return {
    id: job.id,
    type: 'image',
    content: toPersistableMediaUrl(raw) ?? raw,
    prompt: job.prompt || undefined,
    createdAt: job.createdAt ? new Date(job.createdAt).getTime() : Date.now(),
  };
}

/** 生成任务（视频）结果 → 统一的插入素材形态 */
function videoJobToAsset(job: VideoJobUserView): SavedAsset {
  const raw = job.resultUrl || job.playUrl || '';
  return {
    id: job.id,
    type: 'video',
    content: toPersistableMediaUrl(raw) ?? raw,
    coverUrl: toPersistableMediaUrl(job.coverUrl || job.coverProxyUrl),
    durationSec: job.duration ?? null,
    prompt: job.prompt || undefined,
    createdAt: job.createdAt ? new Date(job.createdAt).getTime() : Date.now(),
  };
}

const FILTER_LABELS: Record<FilterValue, string> = {
  all: '全部',
  image: '图片',
  video: '视频',
  text: '文本',
};

export function AssetPickerModal({ open, onClose, assetFilter, onPick }: AssetPickerModalProps) {
  const [assets, setAssets] = useState<SavedAsset[]>([]);
  const [filter, setFilter] = useState<FilterValue>('all');
  const [scope, setScope] = useState<AssetScope>('global');
  const [taskAssets, setTaskAssets] = useState<SavedAsset[]>([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const projectId = getBoardState().projectId;

  const effectiveFilter: FilterValue = assetFilter ?? filter;
  const videoOnly = effectiveFilter === 'video';

  // 每次打开都重读素材与推荐 tab：工具栏入口是常驻挂载（open 只控制渲染），
  // 不重读会看不到收藏后的新增；推荐 tab 也随当前项目重算
  useEffect(() => {
    if (!open) return;
    setAssets(assetStorage.getAll());
    const pid = getBoardState().projectId;
    setScope(pid && assetStorage.getByProject(pid).length > 0 ? 'project' : 'global');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // 「所有素材」tab：懒加载当前账号的生成结果（已完成且有结果），每次切入刷新。
  // 限定视频时只拉视频任务，否则只拉图片任务
  useEffect(() => {
    if (!open || scope !== 'tasks') return;
    let cancelled = false;
    setTasksLoading(true);
    const request = videoOnly
      ? videoApi
          .listMyJobs({ status: 'completed', pageSize: 50 })
          .then((res) => (res.data || [])
            .filter((job) => job.resultUrl || job.playUrl)
            .map(videoJobToAsset))
      : api
          .getImageList()
          .then((res) => (res.data || [])
            .filter((job) => job.status === 'completed' && job.resultUrl)
            .map(jobToAsset));
    request
      .then((list) => {
        if (!cancelled) setTaskAssets(list);
      })
      .catch(() => {
        if (!cancelled) setTaskAssets([]);
      })
      .finally(() => {
        if (!cancelled) setTasksLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, scope, videoOnly]);

  if (!open) return null;

  const scoped = scope === 'project' && projectId
    ? assets.filter((a) => a.projectId === projectId)
    : assets;
  const filtered = effectiveFilter === 'all' ? scoped : scoped.filter((a) => a.type === effectiveFilter);

  const handleInsert = (asset: SavedAsset) => {
    if (onPick) {
      onPick(asset);
    } else if (asset.type === 'video') {
      boardActions.addNode(
        makeNode('video', { x: 100, y: 100 }, {
          jobId: null,
          videoUrl: asset.content,
          coverUrl: asset.coverUrl ?? null,
          status: 'completed',
          prompt: asset.prompt,
          videoDurationSec: asset.durationSec ?? null,
        })
      );
    } else if (asset.type === 'image') {
      const node = makeNode('image', { x: 100, y: 100 }, {
        imageUrl: asset.content,
        status: 'completed',
        prompt: asset.prompt,
      });
      boardActions.addNode(node);
    } else {
      const node = makeNode('text', { x: 100, y: 100 }, {
        content: asset.content,
      });
      boardActions.addNode(node);
    }
    onClose();
  };

  const handleDelete = (id: string) => {
    assetStorage.remove(id);
    setAssets(assetStorage.getAll());
  };

  /** 素材内容区（收藏 tab）：blob: 是会话级链接，旧会话收藏的素材已失效，给占位而非破图 */
  const renderAssetPreview = (asset: SavedAsset) => {
    if (asset.content.startsWith('blob:')) {
      return (
        <div className="flex flex-col items-center gap-1 px-2 text-center text-[10px] text-slate-400">
          {asset.type === 'video' ? <Film className="h-5 w-5" /> : <ImageIcon className="h-5 w-5" />}
          <span>链接已失效</span>
          <span className="text-slate-300">（会话级素材，可删除）</span>
        </div>
      );
    }
    if (asset.type === 'video') {
      return asset.coverUrl ? (
        <img src={resolveBrowserMediaUrl(asset.coverUrl)} alt="" className="h-full w-full object-cover" />
      ) : (
        <video
          src={resolveBrowserMediaUrl(asset.content)}
          className="h-full w-full object-cover"
          muted
          preload="metadata"
        />
      );
    }
    if (asset.type === 'image') {
      return <img src={resolveBrowserMediaUrl(asset.content)} alt="" className="h-full w-full object-cover" />;
    }
    return (
      <div className="flex items-center gap-1 px-2 text-xs text-slate-500">
        <Type className="h-3 w-3" />
        <span className="line-clamp-3">{asset.content.slice(0, 60)}</span>
      </div>
    );
  };

  // portal 到 body：从节点内打开时，节点处于画布 transform 层内，
  // fixed 定位会被 transform 祖先劫持（缩放/平移后弹窗跑飞），必须脱离该包含块
  return createPortal(
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div
        className="flex h-[480px] w-[560px] max-w-[90vw] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 标题栏 */}
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
          <h3 className="text-sm font-semibold text-slate-900">我的素材</h3>
          <button onClick={onClose} className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* 范围 tab：本项目素材 / 全局素材 / 所有素材（任务结果） */}
        <div className="flex items-center gap-1 border-b border-slate-100 px-5 py-2">
          {([
            ['project', '本项目素材', FolderOpen],
            ['global', '全局素材', Globe],
            ['tasks', videoOnly ? '生成视频' : '所有素材', Images],
          ] as const).map(([value, label, Icon]) => (
            <button
              key={value}
              type="button"
              onClick={() => setScope(value)}
              className={`flex items-center gap-1 rounded-md px-3 py-1 text-xs transition ${
                scope === value ? 'bg-blue-500 text-white' : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              <Icon className="h-3 w-3" />
              {label}
            </button>
          ))}
        </div>

        {/* 类型筛选（任务结果 tab 类型已确定，隐藏切换；外部锁定类型时同样隐藏） */}
        <div className="flex items-center gap-2 border-b border-slate-100 px-5 py-2">
          {scope !== 'tasks' &&
            !assetFilter &&
            (['all', 'image', 'video', 'text'] as FilterValue[]).map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setFilter(f)}
                className={`rounded-md px-3 py-1 text-xs transition ${
                  filter === f ? 'bg-blue-500 text-white' : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                {FILTER_LABELS[f]}
              </button>
            ))}
          <span className="ml-auto text-xs text-slate-400">
            {scope === 'tasks' ? taskAssets.length : filtered.length} 项
          </span>
        </div>

        {/* 素材列表 */}
        <div className="flex-1 overflow-y-auto p-4">
          {scope === 'tasks' ? (
            tasksLoading ? (
              <div className="flex h-full items-center justify-center text-slate-400">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : taskAssets.length === 0 ? (
              <div className="flex h-full flex-col items-center justify-center gap-2 text-slate-400">
                <Images className="h-8 w-8" />
                <span className="text-sm">还没有生成结果</span>
                <span className="text-xs">
                  {videoOnly ? '在生视频节点生成视频后，会出现在这里' : '在生图节点生成图片后，会出现在这里'}
                </span>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-3">
                {taskAssets.map((asset) => (
                  <div
                    key={asset.id}
                    className="group relative flex flex-col overflow-hidden rounded-lg border border-slate-200 bg-slate-50 transition hover:border-blue-300 hover:shadow-md"
                    title={asset.prompt || ''}
                  >
                    <div className="flex h-24 items-center justify-center overflow-hidden">
                      {asset.type === 'video' && !asset.coverUrl ? (
                        <video
                          src={resolveBrowserMediaUrl(asset.content)}
                          className="h-full w-full cursor-pointer object-cover"
                          muted
                          preload="metadata"
                          onClick={() => handleInsert(asset)}
                        />
                      ) : (
                        <img
                          src={resolveBrowserMediaUrl(asset.coverUrl || asset.content)}
                          alt={asset.prompt || ''}
                          className="h-full w-full cursor-pointer object-cover"
                          onClick={() => handleInsert(asset)}
                        />
                      )}
                    </div>
                    <div className="flex items-center justify-between border-t border-slate-100 px-2 py-1.5">
                      <span className="max-w-[calc(100%-30px)] truncate text-[10px] text-slate-400" title={asset.prompt || ''}>
                        {asset.prompt || '(无提示词)'}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleInsert(asset)}
                        className="rounded p-1 text-blue-500 hover:bg-blue-50"
                        title="插入到画布"
                      >
                        <Plus className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : filtered.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center gap-2 text-slate-400">
              {videoOnly ? <Film className="h-8 w-8" /> : <ImageIcon className="h-8 w-8" />}
              <span className="text-sm">
                {scope === 'project' && projectId ? '本项目暂无素材' : '暂无素材'}
              </span>
              <span className="text-xs">
                {scope === 'project' && projectId
                  ? '在本画布的图片或视频节点上点击"收藏"，会自动归入本项目'
                  : '在图片或视频节点上点击"收藏"按钮添加'}
              </span>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-3">
              {filtered.map((asset) => (
                <div
                  key={asset.id}
                  className="group relative flex flex-col overflow-hidden rounded-lg border border-slate-200 bg-slate-50 transition hover:border-blue-300 hover:shadow-md"
                >
                  <div className="flex h-24 items-center justify-center overflow-hidden">
                    {renderAssetPreview(asset)}
                  </div>
                  {/* 操作栏 */}
                  <div className="flex items-center justify-between border-t border-slate-100 px-2 py-1.5">
                    <span className="text-[10px] text-slate-400">
                      {formatBeijingDate(asset.createdAt)}
                    </span>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => handleInsert(asset)}
                        disabled={asset.content.startsWith('blob:')}
                        className="rounded p-1 text-blue-500 hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-30"
                        title={asset.content.startsWith('blob:') ? '链接已失效，无法插入' : '插入到画布'}
                      >
                        <Plus className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(asset.id)}
                        className="rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-500"
                        title="删除"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
